import type { ProLayoutProps } from '@ant-design/pro-components';
import { type ThemeConfig, theme } from 'antd';

export type NavTheme = 'light' | 'realDark';
export const NAV_THEME_STORAGE_KEY = 'app_nav_theme';

export const getInitialNavTheme = (): NavTheme => {
  if (typeof window === 'undefined') return 'realDark';
  try {
    const stored = window.localStorage.getItem(NAV_THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'realDark') return stored;
    // Seed the shared provider's existing key; do not create a second theme store.
    window.localStorage.setItem(NAV_THEME_STORAGE_KEY, 'realDark');
  } catch {
    // Storage may be unavailable in embedded/private browsing contexts.
  }
  return 'realDark';
};

export const getNavigationToken = (
  navTheme: NavTheme,
): ProLayoutProps['token'] => {
  const dark = navTheme === 'realDark';
  const background = dark ? '#1E1E1E' : '#F0F0F3';
  const text = dark ? 'rgba(255,255,255,0.88)' : '#1F2633';
  const muted = dark ? 'rgba(255,255,255,0.65)' : '#667085';
  const hover = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  const selected = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
  return {
    header: {
      heightLayoutHeader: 44,
      colorBgHeader: background,
      colorHeaderTitle: text,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorBgMenuItemHover: 'transparent',
      colorBgMenuItemSelected: 'transparent',
    },
    sider: {
      colorMenuBackground: background,
      colorTextMenu: muted,
      colorTextMenuSelected: text,
      colorTextMenuActive: text,
      colorBgMenuItemHover: hover,
      colorBgMenuItemSelected: selected,
      colorTextCollapsedButton: muted,
      colorTextCollapsedButtonHover: text,
      colorBgCollapsedButton: background,
    },
  };
};

export const getAppTheme = (navTheme: NavTheme): ThemeConfig => {
  const dark = navTheme === 'realDark';
  const button = dark ? '#2B2B2B' : '#EEEEEE';
  const hover = dark ? '#363636' : '#E2E2E2';
  const active = dark ? '#414141' : '#D6D6D6';
  const text = dark ? 'rgba(255,255,255,0.88)' : '#1F2633';
  return {
    inherit: false,
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    cssVar: { key: `workbench-${navTheme}` },
    token: {
      colorPrimary: dark ? '#1677ff' : '#0958d9',
      colorPrimaryHover: dark ? '#1f6bc8' : '#0847b2',
      colorPrimaryActive: dark ? '#154a91' : '#003ea3',
      colorBgLayout: dark ? '#121212' : '#FFFFFF',
      colorBgContainer: dark ? '#1E1E1E' : '#F6F7F9',
      colorBgElevated: dark ? '#252525' : '#FFFFFF',
      colorText: text,
      borderRadius: 8,
      boxShadow: 'none',
      boxShadowSecondary: 'none',
      fontFamily:
        "AlibabaSans, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    components: {
      Button: {
        defaultBg: button,
        defaultColor: text,
        defaultBorderColor: 'transparent',
        defaultHoverBg: hover,
        defaultHoverColor: text,
        defaultHoverBorderColor: 'transparent',
        defaultActiveBg: active,
        defaultActiveColor: text,
        defaultActiveBorderColor: 'transparent',
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        ghostBg: 'transparent',
        defaultGhostColor: text,
        defaultGhostBorderColor: 'transparent',
      },
      Card: { headerBg: 'transparent' },
      Table: { headerBg: dark ? '#252525' : '#F6F7F9' },
      Modal: {
        contentBg: dark ? '#1E1E1E' : '#FFFFFF',
        headerBg: dark ? '#1E1E1E' : '#FFFFFF',
      },
    },
  };
};
